import React, { useContext } from 'react';
import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { TvContext } from '@smart-tv/contexts';
import testIds from '@smart-tv/e2e/shared/variables';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import useDialog from '@smart-tv/hooks/use-dialog';
import { dependencies, Props } from './settings-types';
import styles from './settings.module.scss';
import { tabs } from './settings';
import GDPRDescription from '@smart-tv/components/gdpr-dialog/gdpr-description';
import ListItem from '@smart-tv/components/list';
import { HEADER_SETTINGS } from '@smart-tv/components/header/header-helpers';
import { retrievePersistentData } from '@smart-tv/utils/persistent-data/persistent-data';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
import { IS_WEBOS } from '@smart-tv/utils/is-webos';

export const SettingsContent = (props: Props) => {
  const { selectedTab } = props;

  const { ref, focusKey } = useFocusable({
    focusable: selectedTab === tabs.TECHNICAL || selectedTab === tabs.OPEN_SOURCE,
    saveLastFocusedChild: true,
    isFocusBoundary: true,
    focusBoundaryDirections: ['up']
  });

  const { appVersion, model, connectionType, platform, platformVersion } = useContext(TvContext);

  const buildNumber = process.env.BUILD_NUMBER ?? '';

  const lastLoginEmail = retrievePersistentData<string>('last-login-email');
  const customerId = retrievePersistentData<string>('customerId');

  const { opened: dialogOpen } = useDialog();

  const handleKeyPress = React.useCallback(
    (event: KeyboardEvent) => {
      if (isBackKey(event) && !dialogOpen) {
        setFocus(HEADER_SETTINGS);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    },
    [dialogOpen]
  );

  const scrollingRef = React.useRef<HTMLDivElement>(null);

  const scrolling = React.useCallback(
    ({ node }: { node: HTMLElement }) => {
      if (scrollingRef.current != null) {
        // Use the node's own offsetTop (stable) rather than the scroll-relative y from the layout,
        // which would compound drift across successive focus moves.
        scrollingRef.current.scrollTo({
          top: node.offsetTop - 250,
          behavior: 'auto'
        });
      }
    },
    [scrollingRef]
  );

  useEventListener('keydown', (event: KeyboardEvent) => handleKeyPress(event));

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['content']} ref={ref}>
        {selectedTab === tabs.TECHNICAL && (
          <div className={styles['technical-info']}>
            <ListItem
              data={['UserReferenceID', customerId ?? '']}
              listItemProps={{ 'data-testid': testIds.settings.techTable.userReferenceId }}
            />
            <ListItem
              data={['Email', lastLoginEmail ?? '']}
              listItemProps={{ 'data-testid': testIds.settings.techTable.email }}
            />
            {(window.tizen || IS_WEBOS) && model && (
              <ListItem
                data={['Hardware model', model ?? '']}
                listItemProps={{ 'data-testid': testIds.settings.techTable.hardwareModel }}
              />
            )}

            {(window.tizen || IS_WEBOS) && (
              <ListItem
                data={['OS version', platformVersion ? platform + ' ' + platformVersion : '']}
                listItemProps={{ 'data-testid': testIds.settings.techTable.osVersion }}
              />
            )}
            <ListItem
              data={['App version', appVersion ?? '']}
              listItemProps={{ 'data-testid': testIds.settings.techTable.appVersion }}
            />
            {appVersion && buildNumber && (
              <ListItem
                data={['App version, detaljeret', appVersion + ' (' + buildNumber + ')']}
                listItemProps={{ 'data-testid': testIds.settings.techTable.detailedAppVersion }}
              />
            )}
            {window.tizen && connectionType && (
              <ListItem
                data={['Netværk', connectionType ?? '']}
                listItemProps={{ 'data-testid': testIds.settings.techTable.connectionType }}
              />
            )}

            {/* this still can be relevant when debugging
             {displayDeviceSpecs && (
              <>
                <ListItem data={['Available processors', getHardwareConcurrency()?.toString()]} />
                <ListItem data={['RAM', getDeviceMemory()]} />
                <ListItem data={['Bandwidth estimate', getConnectionDownlink()]} />
              </>
            )} */}
          </div>
        )}

        {selectedTab === tabs.OPEN_SOURCE && (
          <div
            className={styles['open-source-container']}
            ref={scrollingRef}
            data-testid={testIds.settings.openSourceTable.table}
          >
            {dependencies.map((data, index) => (
              <ListItem
                key={index}
                onFocus={scrolling}
                data={data}
                listItemProps={{
                  'data-testid': testIds.settings.openSourceTable.row + index.toString() + '_row'
                }}
              />
            ))}
          </div>
        )}

        {selectedTab === tabs.GDPR && (
          <div className={styles['gdpr-container']}>
            <GDPRDescription openedFromSettings={true} />
          </div>
        )}
      </div>
    </FocusContext.Provider>
  );
};

export default SettingsContent;
